import { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import type { AudioFeatureType } from '../../api/cc-smarts/models/AudioFeature';
import { generateAudioShareLink } from '../../common/audioSharingUtils';
import type { ContentDetails } from '../../types/activeContent';
import { useFlags } from '../context/FlagsProviderContext';

const messages = defineMessages({
	copyLinkSuccess: {
		id: 'ai-audio.audio-share-link.copy-success-title',
		defaultMessage: 'Link copied',
		description: 'Title shown when an audio share link is copied',
	},
	copyLinkSuccessDescription: {
		id: 'ai-audio.audio-share-link.copy-success-description',
		defaultMessage: 'People with access to the content can open the audio.',
		description: 'Description shown when an audio share link is copied',
	},
	copyLinkError: {
		id: 'ai-audio.audio-share-link.copy-error',
		defaultMessage: 'Failed to copy link',
		description: 'Error message shown when copying the audio share link fails',
	},
});

export const useAudioShareLinkCopy = () => {
	const { formatMessage } = useIntl();
	const flags = useFlags();

	const copyAudioShareLinkToClipboard = useCallback(
		async (
			mainSourceUrl: string,
			feature: AudioFeatureType,
			supportingContent: ContentDetails[],
		): Promise<void> => {
			try {
				const link = generateAudioShareLink(mainSourceUrl, feature, supportingContent);
				await navigator.clipboard.writeText(link);
				if (flags) {
					flags.showFlag({
						type: 'success-circle',
						title: formatMessage(messages.copyLinkSuccess),
						description: formatMessage(messages.copyLinkSuccessDescription),
						close: 'auto',
					});
				}
			} catch {
				if (flags) {
					flags.showFlag({
						type: 'error',
						title: formatMessage(messages.copyLinkError),
						close: 'auto',
					});
				}
			}
		},
		[flags, formatMessage],
	);

	return { copyAudioShareLinkToClipboard };
};
